import { startTransition, useEffect, useState } from 'react';

/**
 * @typedef {{
 *   offsetHeight: number | undefined;
 *   offsetWidth: number | undefined;
 *   clientHeight: number | undefined;
 *   clientWidth: number | undefined;
 *   scrollWidth: number | undefined;
 *   scrollHeight: number | undefined;
 * }} Dimensions
 */

/** @type {Dimensions} */
const initialDimensions = {
  offsetHeight: undefined,
  offsetWidth: undefined,
  clientHeight: undefined,
  clientWidth: undefined,
  scrollHeight: undefined,
  scrollWidth: undefined,
};

/**
 * A custom hook that returns the dimensions of the ref.
 * Uses resizeObserver to provide ongoing updates
 *
 * @param {import('react').RefObject<HTMLElement>} ref
 */
export function useResizeDimensions(ref) {
  const [dimensions, setDimensions] = useState(initialDimensions);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    /** */
    const updateDimensions = () => {
      startTransition(() => {
        setDimensions({
          offsetHeight: element.offsetHeight,
          offsetWidth: element.offsetWidth,
          clientHeight: element.clientHeight,
          clientWidth: element.clientWidth,
          scrollHeight: element.scrollHeight,
          scrollWidth: element.scrollWidth,
        });
      });
    };

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(element);

    updateDimensions();

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref]);

  return dimensions;
}
